import React, { MutableRefObject, useState } from 'react';
import { Mutation } from '@apollo/client/react/components';
import { useMutation } from '@apollo/client';
import classNames from 'classnames/bind';

import { useCommunityContext } from 'components/CommunityContext';
import UserDetail from 'components/UserDetail';
import ReactionButton from 'components/ReactionButton';
import ConfirmDeleteModal from 'components/ConfirmDeleteModal';
import PostActions from 'components/PostActions';
import Icon from 'koba/components/Icon';
import { toaster } from '@thinkific/toga-react';
import { rawId } from 'utils/rawId';
import { genericToastAlert } from 'utils/toastHelpers';
import { canDeleteComment, canEditPost } from 'utils/userHelpers';
import { relativeFormattedDateTime } from 'helpers/dateHelpers';
import { useWindowSize } from 'hooks/useWindowSize';
import {
  DELETE_REPLY_MUTATION,
  FOLLOW_REPLY_MUTATION,
  UNFOLLOW_REPLY_MUTATION,
} from 'schema/Replies/mutations';
import {
  BREAKPOINT_MD,
  EVENT_POST_TYPES,
  SUCCESS_TOAST,
  REACTION_TYPES,
  NUMBER_OF_REACTIONS,
} from 'utils/constants';
import { DANGER_TYPE } from 'koba/components/InlineActions/constants';
import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';

import { getReactors } from 'helpers/reactionHelpers';
import {
  MentionedUsersFieldFragment,
  ReplyEdgeFieldsFragment as CommentType,
  UserFieldsFragment as ReplyListUser,
} from '__generated__/graphql/legacy/graphql';
import { TFunction } from 'next-i18next';

import { PostAction } from 'types/post';
import ExpandableContent from 'components/Post/components/ExpandableContent';
import { Button } from '@thinkific/chalk';
import { ArrowArcLeft } from '@thinkific/chalk/react-icons';
import { useReplyEditorContext } from 'components/ReplyEditorContext/ReplyEditorContext';
import { Attachment } from 'schema/Post/types';
import styles from './Comment.module.scss';
import ReplyThread from '../ReplyThread/ReplyThread';

const cx = classNames.bind(styles);

interface CommentProps {
  comment: CommentType;
  commentRef?: MutableRefObject<HTMLDivElement | null> | null;
  content: string;
  fetchMoreReplies?: () => void;
  hasReplies?: boolean;
  isFollowingThread?: boolean;
  isReply?: boolean;
  mentionedUsers?: MentionedUsersFieldFragment | null;
  parentPostId: string;
  parentReplyId?: string | null;
  t: TFunction;
  totalReplyCount?: number;
  user: ReplyListUser;
  onCommentDeleted?: () => void;
  onPostChange?: () => void;
}

const Comment = ({
  comment,
  commentRef = null,
  content,
  hasReplies = false,
  isFollowingThread = false,
  isReply = false,
  mentionedUsers,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onCommentDeleted = () => {},
  parentPostId,
  parentReplyId = null,
  t,
  totalReplyCount = 0,
  user,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onPostChange = () => {},
  fetchMoreReplies,
}: CommentProps) => {
  const { currentUser, trackEvent } = useCommunityContext();
  const { updateReplyData, startEditing } = useReplyEditorContext();
  const [isOpen, setIsOpen] = useState(false);
  const [shouldClampContent, setShouldClampContent] = useState<boolean>(
    (content?.length || 0) > 480
  );
  const [unfollowReply, { loading: unfollowLoading }] = useMutation(
    UNFOLLOW_REPLY_MUTATION
  );
  const [followReply, { loading: followLoading }] = useMutation(
    FOLLOW_REPLY_MUTATION
  );

  const {
    ATTACHMENT,
    COMMENT_ID,
    COMMENT_ID_ENCODED,
    POST_TYPE,
    POST_ID,
    POST_ID_ENCODED,
  } = TrackingProperty;

  const trackCommentEvent = (event) =>
    trackEvent(event, {
      [ATTACHMENT]: hasAttachment
        ? comment?.postAttachments?.[0].attachmentType
        : null,
      [COMMENT_ID]: rawId(comment.id),
      [COMMENT_ID_ENCODED]: comment.id,
      [POST_ID]: isReply ? rawId(parentReplyId || '') : rawId(parentPostId),
      [POST_ID_ENCODED]: isReply ? parentReplyId : parentPostId,
      [POST_TYPE]: isReply ? EVENT_POST_TYPES.REPLY : EVENT_POST_TYPES.COMMENT,
    });

  const creationDateTime = relativeFormattedDateTime(
    comment.createdAt,
    currentUser?.locale,
    t
  );

  const canUserDeleteComment = canDeleteComment(currentUser, user);
  const canUserEditPost = canEditPost(currentUser, user);

  const { width } = useWindowSize();
  const isMobile = width < BREAKPOINT_MD;

  const handleDeleteComment = () => {
    document.body.style.overflow = 'hidden';
    setIsOpen(true);
  };

  const closeModal = () => {
    document.body.style.overflow = 'visible';
    setIsOpen(false);
  };

  const handleConfirmDelete = (mutation) => async () => {
    try {
      const {
        data: deleteReplyData,
        errors: deleteReplyError,
      } = await mutation({
        variables: {
          id: comment.id,
        },
        update(cache) {
          // Because it updates the state, closeModal() needs to be called before we update
          // the cache, as that will result in unmounting the component.
          closeModal();

          const commentCacheId = cache.identify({
            __typename: 'Reply',
            id: comment.id,
          });
          const parentCacheId = cache.identify({
            __typename: parentReplyId ? 'Reply' : 'Post',
            id: parentReplyId || parentPostId,
          });
          cache.modify({
            id: parentCacheId,
            fields: {
              replies(
                existingReplies = {
                  totalCount: undefined,
                }
              ) {
                return {
                  ...existingReplies,
                  totalCount: (existingReplies.totalCount || 0) - 1,
                };
              },
            },
          });
          cache.evict({ id: commentCacheId });
          cache.gc();
        },
      });
      onCommentDeleted();
      onPostChange?.();
      trackCommentEvent(TrackingEvent.DELETE_POST);

      const userErrors = deleteReplyData?.deleteReply?.userErrors;
      if (!userErrors?.length && !deleteReplyError?.length) {
        toaster.notice(
          isReply
            ? t(SUCCESS_TOAST.DELETE_REPLY)
            : t(SUCCESS_TOAST.DELETE_COMMENT)
        );
      } else {
        genericToastAlert();
      }
    } catch {
      genericToastAlert();
    }
  };

  const handleEditComment = () => {
    startEditing(comment);
  };

  const onReplyClick = () => {
    updateReplyData(comment.id, comment.content || user.fullName || '');
  };

  const commentActionsList: PostAction[] = [];
  const commentId = isReply ? parentReplyId : comment.id;
  const isFollowingComment = isReply ? isFollowingThread : comment.isFollowing;

  if (isFollowingComment && !unfollowLoading) {
    commentActionsList.push({
      name: 'unfollow',
      text: isReply ? t('Unfollow thread') : t('Unfollow comment'),
      onClick: async () => {
        await unfollowReply({
          variables: {
            id: commentId,
            numberOfReactions: NUMBER_OF_REACTIONS,
          },
        });
        trackCommentEvent(TrackingEvent.UNFOLLOW_COMMENT);
      },
    });
  } else if (!followLoading) {
    commentActionsList.push({
      name: 'follow',
      text: isReply ? t('Follow thread') : t('Follow comment'),
      onClick: async () => {
        await followReply({
          variables: {
            id: commentId,
            numberOfReactions: NUMBER_OF_REACTIONS,
          },
        });
        trackCommentEvent(TrackingEvent.FOLLOW_COMMENT);
      },
    });
  }

  const confirmDeleteBody = isReply
    ? t(
        'components-comment-delete_reply_confirmation',
        'Are you sure you want to delete this reply? You cannot undo this action.'
      )
    : t(
        'components-comment-delete_comment_confirmation',
        'Are you sure you want to delete this comment? You cannot undo this action.'
      );

  const confirmDeleteTitle = isReply ? t('Delete reply') : t('Delete comment');

  if (canUserDeleteComment) {
    commentActionsList.push({
      name: 'delete',
      title: confirmDeleteTitle,
      text: (
        <span className={cx('comment__detail-action')}>
          <Icon className={cx('post__detail-action__icon')} name="trash-can" />
          <span>{confirmDeleteTitle}</span>
        </span>
      ),
      onClick: handleDeleteComment,
      type: DANGER_TYPE,
    });
  }

  if (canUserEditPost) {
    commentActionsList.push({
      name: 'edit',
      title: t('common-edit', 'Edit'),
      text: (
        <>
          <span className={cx('comment__detail-action')}>
            <span>{t('common-edit', 'Edit')}</span>
          </span>
        </>
      ),
      onClick: handleEditComment,
    });
  }

  const hasAttachment = comment.postAttachments && comment.postAttachments[0];

  const reactionButtonContent = (
    <ReactionButton
      hasReacted={comment.hasReacted}
      id={comment.id}
      isReactingTo="comment"
      parentPostId={parentPostId}
      parentReplyId={parentReplyId || ''}
      postReactionCount={
        comment.postReactions ? comment.postReactions.totalCount : 0
      }
      reactionType={REACTION_TYPES.LIKE_POST}
      reactors={getReactors(comment)}
      t={t}
    />
  );

  const commentContent = (
    <>
      <div className={cx('comment__detail')}>
        <div className={cx('comment__user-details')}>
          <UserDetail
            author={user}
            editedAt={comment.editedAt}
            isFollowing={comment.isFollowing}
            isReply={isReply}
            time={creationDateTime || ''}
            totalReplyCount={totalReplyCount}
            isReplyPanel
          />
          {!!commentActionsList.length && (
            <>
              <PostActions
                actionsList={commentActionsList}
                isMobile={isMobile}
                t={t}
              />
              <Mutation mutation={DELETE_REPLY_MUTATION}>
                {(mutate, { loading }) => (
                  <ConfirmDeleteModal
                    closeAction={closeModal}
                    confirmDeleteBody={confirmDeleteBody}
                    confirmDeleteTitle={confirmDeleteTitle}
                    deleteAction={handleConfirmDelete(mutate)}
                    isConfirmModalOpen={isOpen}
                    isLoading={loading}
                  />
                )}
              </Mutation>
            </>
          )}
        </div>

        <div className={cx('comment__content')} data-qa="comment__content">
          <ExpandableContent
            attachments={(comment.postAttachments as Attachment[]) || []}
            content={content}
            hasAttachment={!!hasAttachment}
            mentionedUsers={mentionedUsers}
            setShouldClampContent={setShouldClampContent}
            shouldClampContent={shouldClampContent}
            shouldScroll={false}
            t={t}
            isComment
          />
          <div className={cx('comment__content__footer')}>
            <div
              className={cx('comment__content__buttons-wrapper')}
              data-qa="comment__content__buttons-wrapper"
            >
              {reactionButtonContent}
              {!isReply && (
                <Button
                  className={cx('comment-list__reply-btn')}
                  emphasis="low"
                  icon={ArrowArcLeft}
                  iconPosition="lead"
                  size="sm"
                  onClick={onReplyClick}
                >
                  {t('Reply', 'Reply')}
                </Button>
              )}
            </div>
            {!parentReplyId && (
              <ReplyThread
                author={user}
                fetchMoreReplies={fetchMoreReplies}
                hasReplies={hasReplies}
                isFollowingThread={isFollowingThread}
                parentPostId={parentPostId}
                parentReplyId={comment.id}
                replies={comment.replies}
                t={t}
                onPostChange={onPostChange}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div
      className={cx('comment', { reply: isReply })}
      data-qa={`Comment-${comment.id}`}
      ref={commentRef}
    >
      <div className={cx('comment__container')}>{commentContent}</div>
    </div>
  );
};

export default Comment;
